/**
 * @zendir/ui — CategoryProvider
 *
 * Optional React context for sharing a `CategoryPalette` and display label
 * across sibling components in a dashboard layout. Components that are
 * "category-aware" (Timeline, GroundTrackMap, AstroChart, DataTable) can
 * read from this context to resolve colors/labels without each needing
 * explicit palette props.
 *
 * **This context is fully opt-in.** Components continue to work standalone
 * — explicit `color` / `team` props always take precedence over context.
 *
 * @example
 * ```tsx
 * import { CategoryProvider, useCategoryPalette } from '@zendir/ui';
 *
 * const categories = [
 *   { id: 'alpha', label: 'Team Alpha', color: '#2dccff' },
 *   { id: 'bravo', label: 'Team Bravo', color: '#fce83a' },
 * ];
 *
 * // Wrap a section of your dashboard
 * <CategoryProvider categories={categories} categoryLabel="Squads">
 *   <Timeline events={events} />
 *   <GroundTrackMap pins={pins} />
 * </CategoryProvider>
 *
 * // Inside any component
 * function MyWidget() {
 *   const ctx = useCategoryPalette();
 *   if (!ctx) return null; // graceful when no provider
 *   const color = ctx.palette.getColor('alpha');
 *   const label = ctx.categoryLabel; // "Squads"
 * }
 * ```
 */

import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { CategoryPalette } from "../utils/categoryPalette";
import type { CategoryDef } from "../types";

// =============================================================================
// Context value
// =============================================================================

export interface CategoryContextValue {
  /** Palette instance built from the provided categories. */
  palette: CategoryPalette;
  /** All registered category definitions. */
  categories: CategoryDef[];
  /**
   * Human-readable label for the grouping dimension.
   * Defaults to `"Categories"`. Components that support a label override
   * (e.g. Timeline's `teamLabel`) should fall back to this when no
   * explicit prop is provided.
   */
  categoryLabel: string;
}

const CategoryContext = createContext<CategoryContextValue | null>(null);
CategoryContext.displayName = "CategoryContext";

// =============================================================================
// Provider
// =============================================================================

export interface CategoryProviderProps {
  /** Category definitions — each must have `id`, `label`, and `color`. */
  categories: CategoryDef[];
  /**
   * Display label for the grouping dimension, e.g. `"Teams"`, `"Squads"`,
   * `"Subsystems"`. Defaults to `"Categories"`.
   */
  categoryLabel?: string;
  /**
   * Optional custom fallback palette for auto-assigned unknown ids.
   * Defaults to the Astro data-viz palette built into `CategoryPalette`.
   */
  fallbackPalette?: string[];
  children: ReactNode;
}

/**
 * Provides a shared `CategoryPalette` to descendant components.
 *
 * Wrap a dashboard section (not the entire app) so sibling components
 * like Timeline + Map + Charts resolve the same colors for the same ids.
 */
export function CategoryProvider({
  categories,
  categoryLabel = "Categories",
  fallbackPalette,
  children,
}: CategoryProviderProps): React.ReactElement {
  const value = useMemo<CategoryContextValue>(
    () => ({
      palette: new CategoryPalette(categories, fallbackPalette),
      categories,
      categoryLabel,
    }),
    [categories, categoryLabel, fallbackPalette],
  );

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Access the nearest `CategoryProvider`'s palette and label.
 *
 * Returns `null` when no provider is present — components should treat
 * this as "standalone mode" and fall back to explicit props / defaults.
 */
export function useCategoryPalette(): CategoryContextValue | null {
  return useContext(CategoryContext);
}

/**
 * Like `useCategoryPalette()` but throws if no provider is found.
 * Use only when you know a provider is always present.
 */
export function useCategoryPaletteRequired(): CategoryContextValue {
  const ctx = useContext(CategoryContext);
  if (!ctx) {
    throw new Error(
      "useCategoryPaletteRequired() was called outside of a <CategoryProvider>. " +
        "Wrap a parent component with <CategoryProvider categories={[...]}> or " +
        "use useCategoryPalette() for graceful null handling.",
    );
  }
  return ctx;
}
